// Phase 2C.3 — company branding shared by the application shell.
//
// One in-memory cache per organization so the shell does not refetch the
// company identity on every navigation. Invalidated when the acting
// organization changes (``quowada:orgChanged``) or when the settings page saves
// (``quowada:brandingChanged``).

import { useCallback, useEffect, useState } from "react";
import api, { orgStore } from "./api";
import { reportError } from "./errorReporter";

export const BRANDING_CHANGED_EVENT = "quowada:brandingChanged";

const cache = new Map();

export function invalidateBranding(orgId) {
  if (orgId) cache.delete(orgId);
  else cache.clear();
}

export function notifyBrandingChanged() {
  window.dispatchEvent(new Event(BRANDING_CHANGED_EVENT));
}

/** `{ name, logo_url }` for the acting organization, or null while loading. */
export function useCompanyBranding(orgId) {
  const [branding, setBranding] = useState(() =>
    orgId ? cache.get(orgId) || null : null,
  );

  const load = useCallback(
    async (force) => {
      const id = orgId || orgStore.get();
      if (!id) return;
      if (!force && cache.has(id)) {
        setBranding(cache.get(id));
        return;
      }
      try {
        const r = await api.get("/organization/settings");
        const value = {
          name: r.data?.name || null,
          logo_url: r.data?.logo_url || null,
        };
        cache.set(id, value);
        setBranding(value);
      } catch (err) {
        // Non-fatal: the shell falls back to the organization name it already
        // holds from the membership list.
        reportError("branding.load", err);
      }
    },
    [orgId],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(() => {
    const onOrgChanged = () => {
      invalidateBranding();
      load(true);
    };
    const onBrandingChanged = () => {
      invalidateBranding(orgId || orgStore.get());
      load(true);
    };
    window.addEventListener("quowada:orgChanged", onOrgChanged);
    window.addEventListener(BRANDING_CHANGED_EVENT, onBrandingChanged);
    return () => {
      window.removeEventListener("quowada:orgChanged", onOrgChanged);
      window.removeEventListener(BRANDING_CHANGED_EVENT, onBrandingChanged);
    };
  }, [load, orgId]);

  return branding;
}
